/**
 * Booking availability service - availability checks and slot availability.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

export interface SlotAvailability {
  morning: boolean;
  afternoon: boolean;
  fullDay: boolean;
  afternoonStart?: string; // HH:MM — earliest afternoon start (with 1h buffer)
  morningEnd?: string;     // HH:MM — latest morning end (with 1h buffer)
}

export interface SlotAvailabilityResponse {
  success: boolean;
  boatId: string;
  startDate: string;
  endDate: string;
  halfDayCutoff: string;
  availability: Record<string, SlotAvailability>;
}

export const checkAvailability = async (
  boatId: string,
  pickupDate: string,
  returnDate: string,
  pickupTime?: string,
  returnTime?: string
): Promise<{ success: boolean; available: boolean }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/check-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boatId, pickupDate, returnDate, pickupTime, returnTime })
    });

    if (!response.ok) {
      throw new Error(`Failed to check availability: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
};

export interface BatchAvailabilityResponse {
  success: boolean;
  startDate: string;
  endDate: string;
  availability: Record<string, boolean>;
  unavailableCount: number;
  totalChecked: number;
}

export interface DateSuggestion {
  startDate: string;
  endDate: string;
  direction: 'before' | 'after';
  offsetDays: number;
}

export interface NearestAvailableResponse {
  success: boolean;
  boatId: string;
  requestedDates: { startDate: string; endDate: string };
  durationDays: number;
  suggestions: DateSuggestion[];
}

export const checkBatchAvailability = async (
  startDate: string,
  endDate: string,
  boatIds?: string[]
): Promise<BatchAvailabilityResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/batch-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startDate, endDate, boatIds })
    });

    if (!response.ok) {
      throw new Error(`Failed to check batch availability: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking batch availability:', error);
    throw error;
  }
};

export const findNearestAvailable = async (
  boatId: string,
  startDate: string,
  endDate: string,
  maxSearchDays: number = 30
): Promise<NearestAvailableResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/nearest-available`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boatId, startDate, endDate, maxSearchDays })
    });

    if (!response.ok) {
      throw new Error(`Failed to find nearest available: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error finding nearest available:', error);
    throw error;
  }
};

export interface HourlyDayState { date: string; state: 'free' | 'partial' | 'full' | 'closed' }
export interface HourlyMonthAvailabilityResponse {
  success: boolean;
  boatId: string;
  year: number;
  month: number;
  days: HourlyDayState[];
}

export const getHourlyMonthAvailability = async (
  boatId: string, year: number, month: number,
): Promise<HourlyMonthAvailabilityResponse> => {
  const response = await fetch(`${API_BASE_URL}/bookings/hourly-month-availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ boatId, year, month }),
  });
  if (!response.ok) throw new Error(`Failed to get monthly hourly availability: ${response.statusText}`);
  return await response.json();
};

export interface HourlyOccupiedRange { start: string; end: string; reason: string }
export interface HourlyAvailabilityResponse {
  success: boolean;
  boatId: string;
  date: string;
  occupied: HourlyOccupiedRange[];
  dayClosed: boolean;
}

export const getHourlyAvailability = async (boatId: string, date: string): Promise<HourlyAvailabilityResponse> => {
  const response = await fetch(`${API_BASE_URL}/bookings/hourly-availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ boatId, date }),
  });
  if (!response.ok) throw new Error(`Failed to get hourly availability: ${response.statusText}`);
  return await response.json();
};

export const getSlotAvailability = async (
  boatId: string,
  startDate: string,
  endDate: string,
  halfDayCutoff: string = '13:00'
): Promise<SlotAvailabilityResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/bookings/slot-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boatId, startDate, endDate, halfDayCutoff })
    });

    if (!response.ok) {
      throw new Error(`Failed to get slot availability: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting slot availability:', error);
    throw error;
  }
};
